import { Button, StatePanel } from "@moneymap/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useApi } from "./api/ApiProvider";
import { ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage } from "./auth/AuthPages";
import { AnonymousOnly, SessionGuard } from "./auth/AuthProvider";
import { AppShell } from "./components/AppShell";
import { QueryState } from "./components/QueryState";
import {
  ActivityDetailPage,
  ActivityPage,
  CsvImportHistoryPage,
  CsvImportPage,
} from "./routes/ActivityCsvPages";
import { AffordabilityPage, GoalsPage } from "./routes/GoalsAffordabilityPages";
import { ActionDetailPage, CoveragePage } from "./routes/HomeCoveragePages";
import { IterNestHomePage } from "./routes/IterNestHomePage";
import { OnboardingPage } from "./routes/OnboardingPage";
import { PlaidOAuthPage } from "./routes/PlaidOAuthPage";
import { LifeDayNewPage, ObligationDetailPage, PlanPage, SparkPage } from "./routes/PlanSparkPages";
import { QuickSetupPage } from "./routes/QuickSetupPage";
import {
  ConnectionsSettingsPage,
  FeatureFlagsSettingsPage,
  NeedsConfirmationPage,
  NotificationsSettingsPage,
  PrivacySettingsPage,
  RulesSettingsPage,
  SettingsPage,
} from "./routes/SettingsConfirmationPages";

function RootRedirect() {
  const api = useApi();
  const query = useQuery({ queryKey: ["me"], queryFn: () => api.getMe() });
  const profile = query.data?.data;
  return (
    <QueryState
      isPending={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      loadingTitle="Opening your current plan"
    >
      {profile ? (
        <Navigate
          to={
            profile.onboardingState === "complete"
              ? "/home"
              : "/onboarding/" + profile.onboardingState
          }
          replace
        />
      ) : null}
    </QueryState>
  );
}

function CompletedSetupGuard({ children }: { children: ReactNode }) {
  const api = useApi();
  const query = useQuery({ queryKey: ["me"], queryFn: () => api.getMe() });
  const profile = query.data?.data;
  return (
    <QueryState
      isPending={query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      loadingTitle="Opening the household plan"
    >
      {profile?.onboardingState === "complete" ? (
        children
      ) : profile ? (
        <Navigate to={"/onboarding/" + profile.onboardingState} replace />
      ) : null}
    </QueryState>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <StatePanel
      title="That page is not on this path"
      action={
        <Button type="button" onClick={() => navigate("/")}>
          Return to IterNest Money
        </Button>
      }
    >
      No IterNest Money route matches {location.pathname}.
    </StatePanel>
  );
}

export function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <AnonymousOnly>
            <LoginPage />
          </AnonymousOnly>
        }
      />
      <Route
        path="/signup"
        element={
          <AnonymousOnly>
            <SignupPage />
          </AnonymousOnly>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <AnonymousOnly>
            <ForgotPasswordPage />
          </AnonymousOnly>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/"
        element={
          <SessionGuard>
            <RootRedirect />
          </SessionGuard>
        }
      />
      <Route
        path="/onboarding/:step"
        element={
          <SessionGuard>
            <OnboardingPage />
          </SessionGuard>
        }
      />
      <Route
        path="/plaid/oauth"
        element={
          <SessionGuard>
            <PlaidOAuthPage />
          </SessionGuard>
        }
      />
      <Route
        element={
          <SessionGuard>
            <CompletedSetupGuard>
              <AppShell />
            </CompletedSetupGuard>
          </SessionGuard>
        }
      >
        <Route path="/home" element={<IterNestHomePage />} />
        <Route path="/setup" element={<QuickSetupPage />} />
        <Route path="/action/:id" element={<ActionDetailPage />} />
        <Route path="/coverage" element={<CoveragePage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/activity/import" element={<CsvImportPage />} />
        <Route path="/activity/import/history" element={<CsvImportHistoryPage />} />
        <Route path="/activity/:id" element={<ActivityDetailPage />} />
        <Route path="/plan" element={<PlanPage />} />
        <Route path="/obligations/:id" element={<ObligationDetailPage />} />
        <Route path="/spark" element={<SparkPage />} />
        <Route path="/life-days/new" element={<LifeDayNewPage />} />
        <Route path="/goals" element={<GoalsPage />} />
        <Route path="/debts" element={<Navigate to="/goals" replace />} />
        <Route path="/income-events" element={<Navigate to="/goals" replace />} />
        <Route path="/affordability" element={<AffordabilityPage />} />
        <Route path="/needs-confirmation" element={<NeedsConfirmationPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/connections" element={<ConnectionsSettingsPage />} />
        <Route path="/settings/rules" element={<RulesSettingsPage />} />
        <Route path="/settings/privacy" element={<PrivacySettingsPage />} />
        <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="/settings/feature-flags" element={<FeatureFlagsSettingsPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
